import { useRef, useEffect, useState, useCallback } from "react";

/**
 * Horizontal-scroll container with a custom always-visible scrollbar.
 * Native trackpad two-finger scroll works as usual on the inner container.
 * The custom bar never overlaps content — it sits below as a normal block.
 */
export function ScrollableX({
  children,
  className,
  maxHeight,
}: {
  children: React.ReactNode;
  className?: string;
  maxHeight?: string | number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [thumb, setThumb] = useState({ w: 0, x: 0, show: false });
  const dragRef = useRef<{ startX: number; startScroll: number } | null>(null);

  const recalc = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const { scrollWidth, clientWidth, scrollLeft } = el;
    if (scrollWidth <= clientWidth + 1) {
      setThumb((t) => (t.show ? { w: 0, x: 0, show: false } : t));
      return;
    }
    const w = Math.max((clientWidth / scrollWidth) * clientWidth, 40);
    const maxX = clientWidth - w;
    const maxScroll = scrollWidth - clientWidth;
    const x = maxScroll > 0 ? (scrollLeft / maxScroll) * maxX : 0;
    setThumb((t) =>
      t.show && Math.abs(t.w - w) < 1 && Math.abs(t.x - x) < 0.5
        ? t
        : { w, x, show: true },
    );
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    recalc();
    el.addEventListener("scroll", recalc, { passive: true });
    const ro = new ResizeObserver(recalc);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", recalc);
      ro.disconnect();
    };
  }, [recalc]);

  const onThumbDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const el = ref.current;
    if (!el) return;
    dragRef.current = { startX: e.clientX, startScroll: el.scrollLeft };

    const onMove = (ev: MouseEvent) => {
      if (!dragRef.current || !ref.current) return;
      const el = ref.current;
      const maxScroll = el.scrollWidth - el.clientWidth;
      const w = Math.max(
        (el.clientWidth / el.scrollWidth) * el.clientWidth,
        40,
      );
      const maxX = el.clientWidth - w;
      if (maxX <= 0) return;
      const dx = ev.clientX - dragRef.current.startX;
      el.scrollLeft = Math.max(
        0,
        Math.min(
          maxScroll,
          dragRef.current.startScroll + (dx / maxX) * maxScroll,
        ),
      );
    };

    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, []);

  const onTrackClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).dataset.thumb) return;
    const el = ref.current;
    if (!el) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const maxScroll = el.scrollWidth - el.clientWidth;
    const w = Math.max((el.clientWidth / el.scrollWidth) * el.clientWidth, 40);
    const maxX = el.clientWidth - w;
    if (maxX <= 0) return;
    el.scrollLeft =
      Math.max(0, Math.min(1, (clickX - w / 2) / maxX)) * maxScroll;
  }, []);

  return (
    <div
      className={className}
      style={{ display: "flex", flexDirection: "column" }}
    >
      <div ref={ref} style={{ overflowX: "auto", overflowY: maxHeight ? "auto" : undefined, maxHeight, scrollbarWidth: "none" }}>
        {children}
      </div>
      {thumb.show && (
        <div
          style={{
            height: 6,
            margin: "3px 4px",
            position: "relative",
            borderRadius: 3,
            background: "rgb(241 245 249)",
            flexShrink: 0,
            cursor: "default",
          }}
          onClick={onTrackClick}
        >
          <div
            data-thumb="1"
            onMouseDown={onThumbDown}
            style={{
              position: "absolute",
              top: 0,
              height: "100%",
              width: thumb.w,
              transform: `translateX(${thumb.x}px)`,
              background: "rgba(100, 116, 139, 0.4)",
              borderRadius: 3,
              cursor: "grab",
              userSelect: "none",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLDivElement).style.background =
                "rgba(100, 116, 139, 0.65)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLDivElement).style.background =
                "rgba(100, 116, 139, 0.4)";
            }}
          />
        </div>
      )}
    </div>
  );
}
